import { CoreUser } from '../types';

const TOKEN_KEY = 'wandawazi_access_token';
const EXPIRES_AT_KEY = 'wandawazi_token_expires_at';
const REFRESH_TOKEN_KEY = 'wandawazi_refresh_token';
const USER_KEY = 'wandawazi_user';

export interface StoredSession {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
  user: CoreUser;
}

export function decodeTokenClaims(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export const authStorage = {
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  getAccessTokenExpiresAt(): number {
    return Number(localStorage.getItem(EXPIRES_AT_KEY) ?? '0');
  },

  getCachedUser(): CoreUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as CoreUser;
    } catch {
      return null;
    }
  },

  setSession({ accessToken, expiresIn, refreshToken, user }: StoredSession): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (expiresIn > 0) {
      localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000));
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },

  updateTokens(accessToken: string, expiresIn: number, refreshToken: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000));
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  updateCachedUser(user: CoreUser): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  getTokenClaims(): Record<string, unknown> {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return {};
    return decodeTokenClaims(token);
  },

  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};
